import type { UseFormSetError } from 'react-hook-form';
import {
  JEV_BUDGET_SUGGESTED_MICRO_USD,
  bulletsFromLines,
  briefBulletsOf,
  emptyIdealCandidate,
  emptyIdealProfile,
  emptyPlan30,
  emptyPlan90,
  idealCandidateOf,
  idealProfileOf,
  jobDetailsOf,
  plan30Of,
  plan90Of,
  missingForSubmit,
  requestBriefOf,
  requestBriefTitle,
  requestCompletion,
  REQUEST_BRIEF_KEYS,
  SUPPORTED_CURRENCIES,
  toMinorUnits,
  type BriefSection,
  type CreateRequisitionRequest,
  type CurrencyCode,
  type EmploymentType,
  type HireKind,
  type IdealProfile,
  type JobDetail,
  type Plan30,
  type Plan90,
  type RequestCompletion,
  type RequestIdealCandidate,
  type RequestJobDetails,
  type RequisitionDetail,
  type RequisitionPriority,
  type WorkMode,
} from '@nest-recruit/shared';

// The request-to-hire form's values and their mapping to the API — shared by REQ-01
// (S9) and HM-03 (S10), which is the same form stripped for a phone. ADR 0075 adds what
// the requester writes for the job: its Basics (all but the recruiter), the
// responsibilities, the brief's four parts and the guided ideal candidate.

/**
 * The brief's parts as typed: one point per line. ADR 0083: the 30 and 90-day points are no longer typed here (the plans are
 * templates now); a request written before keeps them, carried through unchanged and shown read-only on the plan tabs.
 */
export interface BriefLines {
  readonly why: string;
  readonly achieve: string;
  readonly plan_30: string;
  readonly plan_90: string;
}

export interface FormValues {
  readonly mode: 'existing' | 'new';
  readonly positionId: string | null;
  readonly ventureId: string | null;
  readonly departmentId: string | null;
  readonly title: string;
  readonly slotsRequested: string;
  readonly priority: RequisitionPriority;
  /** Workflow review 2026-10-29: ideally from… */
  readonly targetStartDate: string;
  /** …at the latest by. */
  readonly targetStartLatest: string;
  /** The budget range: from… */
  readonly budget: string;
  /** …up to. */
  readonly budgetMax: string;
  readonly budgetCurrency: CurrencyCode | null;
  /** On an existing position: replacement or additional. */
  readonly hireKind: HireKind | null;
  /** For a replacement: who, from the requester's team. */
  readonly replacesStaffId: string | null;
  /** ADR 0070: the Jev budget asked for, in millionths of a US dollar; null = none asked. A new request starts at the suggested $5. */
  readonly jevBudgetMicroUsd: number | null;
  /** ADR 0075: the role itself. Required to send. */
  readonly responsibilities: string;
  /** "Why we need this role" is the old "why this hire is needed", now points. Required to send. */
  readonly brief: BriefLines;
  /** ADR 0075's four-part text: only a request written before the template has it; carried through unchanged, shown read-only. */
  readonly idealCandidate: RequestIdealCandidate;
  /** ADR 0083: the templates, optional to send, and "This role manages people" (Position tab). */
  readonly idealProfile: IdealProfile;
  readonly plan30: Plan30;
  readonly plan90: Plan90;
  readonly managesPeople: boolean;
  /** The job's Basics. The hiring manager follows the line manager while empty. */
  readonly lineManagerUserId: string | null;
  readonly hiringManagerUserId: string | null;
  readonly publicTitle: string;
  readonly employmentType: EmploymentType | null;
  readonly workMode: WorkMode | null;
  readonly location: string;
  readonly roleFamily: string;
  readonly targetCloseDate: string;
  readonly isConfidential: boolean;
  readonly shiftStart: string | null;
  readonly shiftEnd: string | null;
  readonly shiftDisplayZone: string | null;
  readonly shiftHours: string;
  readonly description: string;
  readonly requirements: string;
}

const NO_BRIEF: BriefLines = { why: '', achieve: '', plan_30: '', plan_90: '' };

export const BLANK: FormValues = {
  mode: 'existing',
  positionId: null,
  ventureId: null,
  departmentId: null,
  title: '',
  slotsRequested: '1',
  priority: 'normal',
  targetStartDate: '',
  targetStartLatest: '',
  budget: '',
  budgetMax: '',
  budgetCurrency: null,
  hireKind: null,
  replacesStaffId: null,
  jevBudgetMicroUsd: JEV_BUDGET_SUGGESTED_MICRO_USD,
  responsibilities: '',
  brief: NO_BRIEF,
  idealCandidate: emptyIdealCandidate(),
  idealProfile: emptyIdealProfile(),
  plan30: emptyPlan30(),
  plan90: emptyPlan90(),
  managesPeople: false,
  lineManagerUserId: null,
  hiringManagerUserId: null,
  publicTitle: '',
  employmentType: null,
  workMode: null,
  location: '',
  roleFamily: '',
  targetCloseDate: '',
  isConfidential: false,
  shiftStart: null,
  shiftEnd: null,
  shiftDisplayZone: null,
  shiftHours: '',
  description: '',
  requirements: '',
};

/** A new request: the requester is the line manager until they say otherwise (lazy default). */
export function blankFor(userId: string | null): FormValues {
  return { ...BLANK, lineManagerUserId: userId };
}

function linesOf(brief: readonly BriefSection[]): BriefLines {
  const lines = (key: (typeof REQUEST_BRIEF_KEYS)[number]): string => briefBulletsOf(brief, key).join('\n');
  return { why: lines('why'), achieve: lines('achieve'), plan_30: lines('plan_30'), plan_90: lines('plan_90') };
}

export function valuesFrom(detail: RequisitionDetail): FormValues {
  const details = jobDetailsOf(detail.jobDetails);
  // An older request had a free-text reason; its lines become the "why" points.
  const brief = requestBriefOf(detail.brief);
  const why = briefBulletsOf(brief, 'why').length > 0 ? brief : brief.map((section) => (section.key === 'why' ? { ...section, bullets: bulletsFromLines(detail.justification ?? '') } : section));
  const followsLine = detail.hiringManagerUserId === null || detail.hiringManagerUserId === details.lineManagerUserId;
  return {
    mode: detail.positionId ? 'existing' : 'new',
    positionId: detail.positionId,
    ventureId: detail.ventureId,
    departmentId: detail.departmentId,
    title: detail.title,
    slotsRequested: String(detail.slotsRequested),
    priority: detail.priority,
    targetStartDate: detail.targetStartDate ?? '',
    targetStartLatest: detail.targetStartLatest ?? '',
    budget: detail.budgetAmountMinor && detail.budgetCurrency ? majorOf(detail.budgetAmountMinor, detail.budgetCurrency) : '',
    budgetMax: detail.budgetMaxMinor && detail.budgetCurrency ? majorOf(detail.budgetMaxMinor, detail.budgetCurrency) : '',
    budgetCurrency: isCurrency(detail.budgetCurrency) ? detail.budgetCurrency : null,
    hireKind: detail.hireKind,
    replacesStaffId: detail.replacesStaffId,
    jevBudgetMicroUsd: detail.jevBudgetRequestedMicroUsd,
    responsibilities: detail.responsibilities ?? '',
    brief: linesOf(why),
    idealCandidate: idealCandidateOf(detail.idealCandidate),
    idealProfile: idealProfileOf(detail.idealProfile),
    plan30: plan30Of(detail.plan30),
    plan90: plan90Of(detail.plan90),
    managesPeople: detail.managesPeople,
    lineManagerUserId: details.lineManagerUserId,
    hiringManagerUserId: followsLine ? null : detail.hiringManagerUserId,
    publicTitle: details.publicTitle ?? '',
    employmentType: details.employmentType,
    workMode: details.workMode,
    location: details.location ?? '',
    roleFamily: details.roleFamily ?? '',
    targetCloseDate: details.targetCloseDate ?? '',
    isConfidential: details.isConfidential,
    shiftStart: details.shiftStart,
    shiftEnd: details.shiftEnd,
    shiftDisplayZone: details.shiftDisplayZone,
    shiftHours: details.shiftHours ?? '',
    description: details.description ?? '',
    requirements: details.requirements ?? '',
  };
}

/** True when the profile or plan has nothing typed in it yet. */
function isUntouched<T>(value: T, empty: T): boolean {
  return JSON.stringify(value) === JSON.stringify(empty);
}

/**
 * Evening review 2026-11-01 item 4 (DECISIONS-LOG 2026-11-02 Q1): "Existing position" also lists the jobs already made, and
 * picking one fills the request in from that job. The seat is the job's own when it can take a request (`requestableSeatIds`);
 * otherwise the job has no seat on record (a job made straight in Jobs) or its seat is taken, so it goes as a new position with
 * the job's title, venture and department. Only empty fields are filled: nothing the requester typed is overwritten. Why we
 * need the role, the dates, how many and the budget are this request's own and are never copied.
 */
export function prefillFromJob(values: FormValues, job: JobDetail, requestableSeatIds: ReadonlySet<string>): FormValues {
  const seat = job.position && requestableSeatIds.has(job.position.id) ? job.position.id : null;
  const text = (current: string, next: string | null): string => (current.trim() === '' ? (next ?? '') : current);
  const achieve = briefBulletsOf(job.brief, 'achieve').join('\n');
  return {
    ...values,
    ...(seat
      ? { mode: 'existing' as const, positionId: seat }
      : { mode: 'new' as const, positionId: null, hireKind: null, replacesStaffId: null, ventureId: job.ventureId, departmentId: job.departmentId, title: job.title }),
    responsibilities: text(values.responsibilities, job.responsibilities),
    brief: { ...values.brief, achieve: text(values.brief.achieve, achieve) },
    idealProfile: isUntouched(values.idealProfile, emptyIdealProfile()) ? idealProfileOf(job.idealProfile) : values.idealProfile,
    plan30: isUntouched(values.plan30, emptyPlan30()) ? plan30Of(job.plan30) : values.plan30,
    plan90: isUntouched(values.plan90, emptyPlan90()) ? plan90Of(job.plan90) : values.plan90,
    managesPeople: values.managesPeople || job.managesPeople,
    lineManagerUserId: values.lineManagerUserId ?? job.lineManagerUserId,
    publicTitle: text(values.publicTitle, job.publicTitle !== job.title ? job.publicTitle : null),
    employmentType: values.employmentType ?? job.employmentType,
    workMode: values.workMode ?? job.workMode,
    location: text(values.location, job.location),
    roleFamily: text(values.roleFamily, job.roleFamily),
    shiftStart: values.shiftStart ?? job.shiftStart,
    shiftEnd: values.shiftStart === null ? job.shiftEnd : values.shiftEnd,
    shiftDisplayZone: values.shiftStart === null ? job.shiftDisplayZone : values.shiftDisplayZone,
    shiftHours: text(values.shiftHours, job.shiftHours),
    description: text(values.description, job.description),
    requirements: text(values.requirements, job.requirements),
  };
}

function textOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

export function briefFrom(lines: BriefLines): BriefSection[] {
  return REQUEST_BRIEF_KEYS.map((key) => ({ key, title: requestBriefTitle(key), bullets: bulletsFromLines(lines[key]) }));
}

export function jobDetailsFrom(values: FormValues): RequestJobDetails {
  return {
    lineManagerUserId: values.lineManagerUserId,
    publicTitle: textOrNull(values.publicTitle),
    employmentType: values.employmentType,
    workMode: values.workMode,
    location: textOrNull(values.location),
    roleFamily: textOrNull(values.roleFamily),
    targetCloseDate: textOrNull(values.targetCloseDate),
    isConfidential: values.isConfidential,
    shiftStart: values.shiftStart,
    shiftEnd: values.shiftStart === null ? null : values.shiftEnd,
    shiftDisplayZone: values.shiftStart === null ? null : values.shiftDisplayZone,
    shiftHours: textOrNull(values.shiftHours),
    description: textOrNull(values.description),
    requirements: textOrNull(values.requirements),
  };
}

/** What the API takes: minor units as a digit string (non-negotiable #7). */
export function requestFrom(values: FormValues): CreateRequisitionRequest {
  const minor = (text: string): string | null => {
    const amount = Number(text.replace(/[\s,]/g, ''));
    const isAmount = text.trim() !== '' && Number.isFinite(amount) && amount >= 0;
    return isAmount && values.budgetCurrency ? toMinorUnits(amount, values.budgetCurrency).toString() : null;
  };
  const hasBudget = values.budget.trim() !== '' || values.budgetMax.trim() !== '';
  const isExisting = values.mode === 'existing';
  const brief = briefFrom(values.brief);
  return {
    positionId: isExisting ? values.positionId : null,
    departmentId: isExisting ? null : values.departmentId,
    title: isExisting ? null : values.title.trim() || null,
    slotsRequested: Number(values.slotsRequested) || 1,
    priority: values.priority,
    targetStartDate: values.targetStartDate || null,
    targetStartLatest: values.targetStartLatest || null,
    budgetAmountMinor: minor(values.budget),
    budgetMaxMinor: minor(values.budgetMax),
    budgetCurrency: hasBudget ? values.budgetCurrency : null,
    hireKind: isExisting ? values.hireKind : null,
    replacesStaffId: isExisting && values.hireKind === 'replacement' ? values.replacesStaffId : null,
    jevBudgetMicroUsd: values.jevBudgetMicroUsd,
    responsibilities: textOrNull(values.responsibilities),
    brief,
    idealCandidate: values.idealCandidate,
    idealProfile: values.idealProfile,
    plan30: values.plan30,
    plan90: values.plan90,
    managesPeople: values.managesPeople,
    jobDetails: jobDetailsFrom(values),
    // The hiring manager follows the line manager unless someone else is chosen (as in the job, "Start R3" Q2).
    hiringManagerUserId: values.hiringManagerUserId ?? values.lineManagerUserId,
  };
}

/**
 * What still stops this form being submitted, as field → message — the shared F4.02 rule
 * the server applies, so the Submit button and the live summary can never disagree.
 */
export function missingFor(values: FormValues): Record<string, string> {
  const body = requestFrom(values);
  const why = briefBulletsOf(briefFrom(values.brief), 'why').join('\n');
  return missingForSubmit({
    positionId: body.positionId ?? null,
    departmentId: body.departmentId ?? null,
    title: body.title ?? null,
    grade: null,
    slotsRequested: body.slotsRequested ?? 1,
    justification: why === '' ? null : why,
    targetStartDate: body.targetStartDate ?? null,
    targetStartLatest: body.targetStartLatest ?? null,
    budgetAmountMinor:
      body.budgetAmountMinor === undefined || body.budgetAmountMinor === null ? null : String(body.budgetAmountMinor),
    budgetMaxMinor: body.budgetMaxMinor === undefined || body.budgetMaxMinor === null ? null : String(body.budgetMaxMinor),
    budgetCurrency: body.budgetCurrency ?? null,
    hireKind: body.hireKind ?? null,
    replacesStaffId: body.replacesStaffId ?? null,
    responsibilities: body.responsibilities ?? null,
    jobDetails: jobDetailsFrom(values),
  });
}

/** The completion bar beside the form (ADR 0075): everything counts, two parts are required; the templates by their boxes (ADR 0083). */
export function completionOf(values: FormValues): RequestCompletion {
  const hasPlace = values.mode === 'existing' ? values.positionId !== null : values.departmentId !== null && values.title.trim() !== '';
  return requestCompletion({
    hasPlace,
    hasStart: values.targetStartDate !== '',
    hasBudget: values.budget.trim() !== '' || values.budgetMax.trim() !== '',
    responsibilities: textOrNull(values.responsibilities),
    brief: briefFrom(values.brief),
    idealProfile: values.idealProfile,
    plan30: values.plan30,
    plan90: values.plan90,
    managesPeople: values.managesPeople,
    details: { location: textOrNull(values.location), workMode: values.workMode, description: textOrNull(values.description), requirements: textOrNull(values.requirements) },
  });
}

function majorOf(minor: string, currency: string): string {
  const divisor = currency === 'BDT' || currency === 'GBP' || currency === 'USD' ? 100 : 1;
  return String(Number(minor) / divisor);
}

export function isCurrency(value: string | null): value is CurrencyCode {
  return SUPPORTED_CURRENCIES.some((currency) => currency === value);
}

/** Server and shared-rule field names that a form field shows under a different name. */
export const FIELD_ALIASES: Readonly<Record<string, string>> = {
  budgetAmountMinor: 'budget',
  budgetMaxMinor: 'budgetMax',
  justification: 'brief.why',
  shift: 'shiftEnd',
};

const FORM_FIELDS = [
  'positionId',
  'departmentId',
  'title',
  'slotsRequested',
  'targetStartDate',
  'targetStartLatest',
  'budget',
  'budgetMax',
  'budgetCurrency',
  'hireKind',
  'replacesStaffId',
  'jevBudgetMicroUsd',
  'responsibilities',
  'brief.why',
  'lineManagerUserId',
  'shiftEnd',
] as const;

/** Server and shared-rule field errors, next to their fields (§L26: inline, never a banner). */
export function applyErrors(
  fields: Readonly<Record<string, string>>,
  setError: UseFormSetError<FormValues>,
): void {
  for (const [name, message] of Object.entries(fields)) {
    const field = FIELD_ALIASES[name] ?? name;
    const known = FORM_FIELDS.find((candidate) => candidate === field);
    if (known) setError(known, { type: 'server', message });
  }
}
